import { useTranslation } from 'react-i18next'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import type { StaatidBucket, AntallTransfersFordeling } from './api'
import { formatDager } from './api'

interface Props {
  buckets: StaatidBucket[]
  antallTransfers: AntallTransfersFordeling[]
  loading: boolean
}

export default function FordelingCharts({ buckets, antallTransfers, loading }: Props) {
  const { t } = useTranslation('staatid')
  if (loading) {
    return (
      <div className="card p-4">
        <div data-testid="chart-skeleton" className="animate-pulse h-56 bg-gray-100 dark:bg-dark-border rounded-sm" />
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="card p-4" data-testid="staatid-bucket-chart">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('fordeling.bucketTitle')}</h3>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={buckets} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip formatter={(v: number) => [v.toLocaleString('nb-NO'), t('common.antall')]} />
            <Bar dataKey="antall" fill="#06b6d4" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="card p-4" data-testid="staatid-antall-transfers">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{t('fordeling.leddTitle')}</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-dark-border">
              <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">{t('fordeling.antallOverdragelser')}</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.antall')}</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600 dark:text-gray-400">{t('common.medianDager')}</th>
            </tr>
          </thead>
          <tbody>
            {antallTransfers.map(row => (
              <tr key={row.antall_transfers} className="border-b border-gray-100 dark:border-dark-border">
                <td className="px-3 py-2">{row.antall_transfers >= 5 ? '5+' : row.antall_transfers}</td>
                <td className="px-3 py-2 text-right tabular-nums">{row.antall.toLocaleString('nb-NO')}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatDager(row.median_dager)}</td>
              </tr>
            ))}
            {antallTransfers.length === 0 && (
              <tr>
                <td colSpan={3} className="px-3 py-4 text-center text-gray-400">{t('common.noData')}</td>
              </tr>
            )}
          </tbody>
        </table>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
          {t('fordeling.leddNote')}
        </p>
      </div>
    </div>
  )
}
